import {
  Button,
  Stack,
  ActionIcon,
  ColorInput,
  Divider,
  Title,
  Select,
  Text,
  Image,
  Group,
  Switch,
} from "@mantine/core";
import { Dropzone, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import { saveAs } from "file-saver";
import {
  IconRefresh,
  IconFileDownload,
  IconPhoto,
  IconUpload,
  IconX,
} from "@tabler/icons-react";
import { jsPDF } from "jspdf";
import "svg2pdf.js";

export const defaultColours = {
  c1: "#ffc23d",
  c2: "#cc6666",
  c3: "#cc66a5",
  c4: "#66d180",
  c5: "#73a9ff",
};

export const defaultPattern = "pentagons";
const patternOptions = [
  { value: "singleColour", label: "Single Colour", data: 1 },
  { value: "pentagons", label: "Pentagons", data: 2 },
  { value: "hexagons", label: "Hexagons", data: 2 },
  { value: "sections", label: "Sections", data: 5 },
  { value: "photo", label: "Photo", data: 0 },
];

export function Controls({
  colours,
  onColoursChange,
  patternType,
  onPatternTypeChange,
  photoDataUrl,
  onPhotoChange,
  showBase,
  onShowBaseChange,
}) {
  function updateColour(key, value) {
    const newColours = { ...colours, [key]: value };
    onColoursChange(newColours);
  }

  const currPatternOption = patternOptions.find((p) => p.value === patternType);
  const colourInputs = [];
  if (currPatternOption && currPatternOption.data) {
    for (let i = 0; i < currPatternOption.data; i++) {
      let label = "Face Colour";
      if (currPatternOption.data > 1) {
        label += ` ${i + 1}:`;
      }

      colourInputs.push(
        <ColorInput
          key={i}
          placeholder="Pick color"
          label={label}
          value={colours[`c${i + 1}`]}
          onChange={(value) => updateColour(`c${i + 1}`, value)}
          rightSection={
            <ActionIcon
              onClick={() => updateColour(`c${i + 1}`, randomColor())}
            >
              <IconRefresh size="1rem" />
            </ActionIcon>
          }
        />,
      );
    }
  }

  return (
    <Stack>
      <Button
        leftIcon={<IconFileDownload />}
        variant="gradient"
        gradient={{ from: "teal", to: "lime" }}
        onClick={() => saveAsPdf()}
      >
        Download as PDF
      </Button>

      <Button
        leftIcon={<IconFileDownload />}
        variant="gradient"
        gradient={{ from: "orange", to: "red" }}
        onClick={() => save_as_svg()}
      >
        Save as SVG
      </Button>

      <Divider my="sm" />

      <Title order={4}>Customise</Title>

      <Select
        label="Choose decoration"
        value={patternType}
        onChange={onPatternTypeChange}
        data={patternOptions}
      />

      <Switch
        label="Show Dome Base Part"
        checked={showBase}
        onChange={(event) => onShowBaseChange(event.currentTarget.checked)}
      />

      <Divider my="sm" variant="dotted" />

      {patternType === "photo" ? (
        <Stack spacing="sm">
          <Dropzone
            onDrop={(files) => {
              if (files.length > 0) {
                const reader = new FileReader();
                reader.onload = (e) => onPhotoChange(e.target.result);
                reader.readAsDataURL(files[0]);
              }
            }}
            accept={IMAGE_MIME_TYPE}
            maxFiles={1}
            multiple={false}
          >
            <Group
              position="center"
              spacing="xl"
              style={{ minHeight: 100, pointerEvents: "none" }}
            >
              <Dropzone.Accept>
                <IconUpload size="2rem" stroke={1.5} />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <IconX size="2rem" stroke={1.5} />
              </Dropzone.Reject>
              <Dropzone.Idle>
                <IconPhoto size="2rem" stroke={1.5} />
              </Dropzone.Idle>

              <div>
                <Text size="sm" inline>
                  {photoDataUrl
                    ? "Drop a new image to replace"
                    : "Drop an image here or click to select"}
                </Text>
              </div>
            </Group>
          </Dropzone>
          {photoDataUrl && (
            <Image
              src={photoDataUrl}
              alt="Uploaded photo preview"
              radius="sm"
              withPlaceholder
            />
          )}
        </Stack>
      ) : (
        colourInputs
      )}
    </Stack>
  );
}

const saveAsPdf = async () => {
  const svgs = document.getElementsByClassName("template-page");
  if (svgs.length === 0) return;

  // Create a new A4 portrait jsPDF instance
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  // Page 1: Dome Shape Net
  await doc.svg(svgs[0], {
    x: 0,
    y: 0,
    width: 210,
    height: 297,
  });

  // Page 2: Dome Base (if present)
  if (svgs.length > 1) {
    doc.addPage("a4", "portrait");
    await doc.svg(svgs[1], {
      x: 0,
      y: 0,
      width: 210,
      height: 297,
    });
  }

  doc.save("geodesic-dome.pdf");
};

const randomColor = () => {
  const h = Math.floor(Math.random() * 360);
  const s = 50;
  const l = 60;

  return `hsl(${h}, ${s}%, ${l}%)`;

  // `#${Math.floor(Math.random() * 16777215).toString(16)}`;
};

const save_as_svg = () => {
  const svgs = document.getElementsByClassName("template-page");
  if (svgs.length === 0) return;

  // If only one SVG, just save it directly (or keep it in A4 layout)
  if (svgs.length === 1) {
    const svgContent = svgs[0].innerHTML;
    let combinedSvg = `<svg viewBox="0 0 210 297" width="100%" xmlns="http://www.w3.org/2000/svg">
      <g transform="translate(0, 0)">
        ${svgContent}
      </g>
    </svg>`;
    var blob = new Blob([combinedSvg], { type: "image/svg+xml" });
    saveAs(blob, "geodesic-dome-shape-net.svg");
    return;
  }

  // Extract innerHTML from both SVGs
  const svg1Content = svgs[0].innerHTML;
  const svg2Content = svgs[1].innerHTML;

  // Combine them into a single A3 Landscape SVG
  // 420mm wide x 297mm high
  let combinedSvg = `<svg viewBox="0 0 420 297" width="100%" xmlns="http://www.w3.org/2000/svg">
    <g transform="translate(0, 0)">
      ${svg1Content}
    </g>
    <g transform="translate(210, 0)">
      ${svg2Content}
    </g>
  </svg>`;

  var blob2 = new Blob([combinedSvg], { type: "image/svg+xml" });
  saveAs(blob2, "geodesic-dome-A3.svg");
};
